<!--
  Compose ONE appointment trigger.

  The document is named in the title rather than repeated in the body: the whole dialog is about
  this one document, and a rule that named it in every sentence would say it four times.

  Every control here came out of the row unchanged, including the read-back sentence and the two
  refusals said at the control that caused them. What is new is that they are drawn once, when a
  practice asks for them, instead of under every document in both sections.
-->
<h1 mat-dialog-title>Send &ldquo;{{ document.name }}&rdquo; for an appointment type</h1>

<div mat-dialog-content>
    <div class="pdt p-t-10">

        <div class="pdt__loading" *ngIf="isLoadingAppointmentTypes" data-test="types-loading">
            <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        </div>

        <!--
          A FAILED READ IS NOT AN EMPTY PRACTICE. The picker is empty either way, so the difference
          has to be said in words — the tab makes the same distinction about its own two lists.
        -->
        <div class="pdt__alert pdt__alert--warn" *ngIf="appointmentTypeError">
            <mat-icon>error_outline</mat-icon>
            <div data-test="types-error">{{ appointmentTypeError }}</div>
        </div>

        <div class="pdt__fields">
            <mat-form-field appearance="outline" class="pdt__field">
                <mat-label>Appointment type</mat-label>
                <mat-select [(ngModel)]="draft.appointmentTypeId"
                    [ngModelOptions]="{ standalone: true }" data-test="trigger-appointment-type">
                    <mat-option *ngFor="let type of appointmentTypes; trackBy: trackById"
                        [value]="type.id">{{ type.type }}</mat-option>
                </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="pdt__field">
                <mat-label>When</mat-label>
                <mat-select [(ngModel)]="draft.sendOn" [ngModelOptions]="{ standalone: true }"
                    data-test="trigger-send-on">
                    <mat-option *ngFor="let option of sendOnOptions" [value]="option.value">
                        {{ option.label }}
                    </mat-option>
                </mat-select>
            </mat-form-field>

            <!--
              §24.2: a trigger that fires when the appointment is BOOKED has no offset at all, so
              there is no control for one and `offsetDays` leaves as 0. The control is removed
              rather than disabled: a disabled field still holds the number that was typed into it
              before the practice changed their mind, and this is the one field where a stale 3
              would be written to a column that must be 0.
            -->
            <ng-container *ngIf="draftHasOffset">
                <mat-form-field appearance="outline" class="pdt__field pdt__field--narrow">
                    <mat-label>How long</mat-label>
                    <input matInput type="number" min="0" step="1"
                        data-test="trigger-offset-value" [(ngModel)]="draft.offsetValue"
                        [ngModelOptions]="{ standalone: true }" />
                </mat-form-field>

                <!--
                  DAYS OR WEEKS — and weeks become days before anything leaves this component
                  (§3.2). The unit is never sent; `toOffsetDays` is the only thing that knows what
                  a week is.
                -->
                <mat-form-field appearance="outline" class="pdt__field pdt__field--narrow">
                    <mat-label>Days or weeks</mat-label>
                    <mat-select [(ngModel)]="draft.offsetUnit"
                        [ngModelOptions]="{ standalone: true }" data-test="trigger-offset-unit">
                        <mat-option value="days">Days</mat-option>
                        <mat-option value="weeks">Weeks</mat-option>
                    </mat-select>
                </mat-form-field>
            </ng-container>
        </div>

        <!--
          The draft, read back in the words the chip will use. Derived from the DAYS that will be
          sent, not from the number in the box — so "3" and "weeks" reads back as "3 weeks before
          the appointment" and a conversion that had gone wrong would say so here, before the
          practice pressed the button.
        -->
        <p class="hint" data-test="trigger-draft-phrase" *ngIf="!draftOffsetProblem">
            Booking this appointment type will send &ldquo;{{ document.name }}&rdquo;
            {{ draftPhrase }}.
        </p>

        <!--
          A DISABLED BUTTON THAT DOES NOT SAY WHY IS A BUG REPORT. This is the client's half of a
          refusal the server would send back anyway, said at the control that caused it instead of
          after a round trip.
        -->
        <p class="hint pdt__problem" data-test="trigger-offset-problem" *ngIf="draftOffsetProblem">
            {{ draftOffsetProblem }}
        </p>

        <!-- The server's refusal, held against the form rather than thrown at a toast. -->
        <div class="pdt__alert pdt__alert--error" *ngIf="saveError">
            <mat-icon>error_outline</mat-icon>
            <div data-test="trigger-error">{{ saveError }}</div>
        </div>
    </div>
</div>

<div mat-dialog-actions class="dialog-actions">
    <button mat-button type="button" data-test="cancel-trigger" (click)="cancel()">Cancel</button>
    <button mat-raised-button color="primary" type="button" data-test="save-trigger"
        [disabled]="!canSave()" (click)="save()">Add trigger</button>
</div>
